import React, { useCallback, useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { FileWithBase64 } from '../../globals/interfaces';

// Add react-easy-crop for manual cropping
// You'll need to install: npm install react-easy-crop
import Cropper from 'react-easy-crop';

const cropAspect = 16/9;

interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface AspectOption {
  value: number | null;
  label: string;
  icon: string;
}

interface DragAndDropFileUploadProps {
    onDone: (fileWithBase64: FileWithBase64) => void;
    autoCrop?: boolean; // Enable/disable auto-cropping
    cropThreshold?: number; // Whitespace threshold (0-255)
    cropPadding?: number; // Padding around cropped content in pixels
    allowManualCrop?: boolean; // Enable manual cropping UI
    cropperWidth?: number; // Width of the cropper container in pixels
    cropperHeight?: number; // Height of the cropper container in pixels
    previewMaxHeight?: number;
}

enum ViewMode {
    UPLOAD = 'upload',
    PREVIEW = 'preview',
    MANUAL_CROP = 'manual_crop',
    FILE_INFO = 'file_info' // New view mode for non-image files
}

const DragAndDropFileUpload: React.FC<DragAndDropFileUploadProps> = ({
    onDone,
    autoCrop = true,
    cropThreshold = 240,
    cropPadding = 10,
    allowManualCrop = true,
    cropperWidth = 600, // Default to a larger cropper width
    cropperHeight = 450, // Default to a larger cropper height
    previewMaxHeight = 400
}) => {
    const [dragging, setDragging] = useState<boolean>(false);
    const [uploaded, setUploaded] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [originalImage, setOriginalImage] = useState<string | null>(null);
    const [currentFile, setCurrentFile] = useState<File | null>(null);
    const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.UPLOAD);
    const [autoCroppedImage, setAutoCroppedImage] = useState<string | null>(null);
    const [finalImage, setFinalImage] = useState<string | null>(null);
    const [isImageFile, setIsImageFile] = useState<boolean>(false);
    
    // State for the manual cropper
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [currentAspect, setCurrentAspect] = useState<number | null>(cropAspect);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
    
    // Predefined aspect ratio options
    const aspectOptions: AspectOption[] = [
        { value: 16/9, label: '16:9', icon: 'fa-rectangle-landscape' },
        { value: 9/16, label: '9:16', icon: 'fa-rectangle-portrait' },
    ];
    
    // Refs for canvas elements used in image processing
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const processedCanvasRef = useRef<HTMLCanvasElement>(null);

    const dragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragging(true);
    }, []);

    const dragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragging(true);
    }, []);

    const dragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragging(false);
    }, []);
    
    // Rotate image by 90 degrees
    const handleRotate = useCallback(() => {
        setRotation((prev) => (prev + 90) % 360);
    }, []);
    
    // Change the aspect ratio
    const handleAspectChange = useCallback((newAspect: number | null) => {
        setCurrentAspect(newAspect);
    }, []);

    // Process the image to detect and crop whitespace
    const processImage = useCallback((imageElement: HTMLImageElement): string => {
        if (!autoCrop || !canvasRef.current || !processedCanvasRef.current) {
            // If auto-crop is disabled or canvas refs aren't ready, 
            // return the original image as base64
            return imageElement.src;
        }

        // Create canvas for original image
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return imageElement.src;

        canvas.width = imageElement.width;
        canvas.height = imageElement.height;
        ctx.drawImage(imageElement, 0, 0);

        // Get image data for processing
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // Find bounds of non-white content
        let minX = canvas.width;
        let minY = canvas.height;
        let maxX = 0;
        let maxY = 0;
        let foundContent = false;

        // Check each pixel to find content bounds
        for (let y = 0; y < canvas.height; y++) {
            for (let x = 0; x < canvas.width; x++) {
                const idx = (y * canvas.width + x) * 4;
                const r = data[idx];
                const g = data[idx + 1];
                const b = data[idx + 2];
                
                // If pixel is not white (using threshold)
                if (r < cropThreshold || g < cropThreshold || b < cropThreshold) {
                    minX = Math.min(minX, x);
                    minY = Math.min(minY, y);
                    maxX = Math.max(maxX, x);
                    maxY = Math.max(maxY, y);
                    foundContent = true;
                }
            }
        }

        // If content was found, crop the image
        if (foundContent && maxX - minX > 10 && maxY - minY > 10) { // Ensure we found meaningful content
            // Add padding to the crop (while staying within image bounds)
            minX = Math.max(0, minX - cropPadding);
            minY = Math.max(0, minY - cropPadding);
            maxX = Math.min(canvas.width - 1, maxX + cropPadding);
            maxY = Math.min(canvas.height - 1, maxY + cropPadding);

            // Calculate crop dimensions
            const cropWidth = maxX - minX + 1;
            const cropHeight = maxY - minY + 1;

            // Create canvas for cropped image
            const processedCanvas = processedCanvasRef.current;
            const processedCtx = processedCanvas.getContext('2d');
            if (!processedCtx) return imageElement.src;
            
            processedCanvas.width = cropWidth;
            processedCanvas.height = cropHeight;

            // Draw cropped region to the new canvas
            processedCtx.drawImage(
                canvas,
                minX, minY, cropWidth, cropHeight,
                0, 0, cropWidth, cropHeight
            );

            // Return the cropped image as base64
            return processedCanvas.toDataURL('image/png');
        } else {
            // No content found or entire image is content, return the original
            return imageElement.src;
        }
    }, [autoCrop, cropThreshold, cropPadding]);

    // Handle the end of cropping, generates a cropped image from croppedAreaPixels
    const createCroppedImage = useCallback(async (imageSrc: string, pixelArea: Area, imageRotation: number) => {
        const image = new Image();
        image.src = imageSrc;
        
        return new Promise<string>((resolve) => {
            image.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                
                if (!ctx) {
                    resolve(imageSrc);
                    return;
                }
                
                // Set canvas dimensions to the size of the cropped area
                const width = pixelArea.width;
                const height = pixelArea.height;
                
                // Rotate dimensions if needed
                const rotateRad = (imageRotation * Math.PI) / 180;
                const rotatedWidth = Math.abs(width * Math.cos(rotateRad)) + Math.abs(height * Math.sin(rotateRad));
                const rotatedHeight = Math.abs(width * Math.sin(rotateRad)) + Math.abs(height * Math.cos(rotateRad));
                
                // Set canvas to final dimensions
                canvas.width = width;
                canvas.height = height;
                
                // Draw the image with transformation
                ctx.save();
                
                // Move to center of canvas
                ctx.translate(width / 2, height / 2);
                // Rotate around center
                ctx.rotate(rotateRad);
                // Draw the cropped image centered
                ctx.drawImage(
                    image,
                    pixelArea.x,
                    pixelArea.y,
                    pixelArea.width,
                    pixelArea.height,
                    -width / 2,
                    -height / 2,
                    width,
                    height
                );
                
                ctx.restore();
                
                // Get the data URL of the canvas content
                resolve(canvas.toDataURL('image/png'));
            };
        });
    }, []);

    // Handle crop complete event
    const onCropComplete = useCallback((croppedArea: any, croppedAreaPixels: Area) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    // Handle manual crop confirmation
    const handleCropConfirm = useCallback(async () => {
        if (!originalImage || !croppedAreaPixels) return;
        
        setProcessing(true);
        try {
            const croppedImageUrl = await createCroppedImage(
                originalImage,
                croppedAreaPixels,
                rotation
            );
            
            setFinalImage(croppedImageUrl);
            
            if (currentFile) {
                onDone({
                    base64: croppedImageUrl,
                    file: {
                        name: currentFile.name,
                        size: currentFile.size,
                        type: currentFile.type,
                        lastModified: currentFile.lastModified,
                    },
                    name: currentFile.name,
                    type: currentFile.type,
                });
            }
            
            setViewMode(ViewMode.PREVIEW);
            setUploaded(true);
        } catch (e) {
            console.error('Error generating cropped image:', e);
        }
        setProcessing(false);
    }, [originalImage, croppedAreaPixels, rotation, currentFile, onDone, createCroppedImage]);

    // Switch to manual cropping from auto-crop preview
    const handleSwitchToManualCrop = useCallback(() => {
        if (allowManualCrop && originalImage) {
            // Reset crop and zoom for a fresh manual crop
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            setRotation(0);
            setCurrentAspect(cropAspect);
            setViewMode(ViewMode.MANUAL_CROP);
        }
    }, [allowManualCrop, originalImage, cropAspect]);

    // Accept the auto-cropped image
    const handleAcceptAutoCrop = useCallback(() => {
        if (autoCroppedImage && currentFile) {
            setFinalImage(autoCroppedImage);
            onDone({
                base64: autoCroppedImage,
                file: {
                    name: currentFile.name,
                    size: currentFile.size,
                    type: currentFile.type,
                    lastModified: currentFile.lastModified,
                },
                name: currentFile.name,
                type: currentFile.type,
            });
            setViewMode(ViewMode.PREVIEW);
            setUploaded(true);
        }
    }, [autoCroppedImage, currentFile, onDone]);

    // Restart the cropping process
    const handleRecrop = useCallback(() => {
        if (originalImage) {
            // Reset to manual cropping
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            setRotation(0);
            setCurrentAspect(cropAspect);
            setViewMode(ViewMode.MANUAL_CROP);
        }
    }, [originalImage, cropAspect]);

    // Reset to upload new file
    const handleNewUpload = useCallback(() => {
        setUploaded(false);
        setOriginalImage(null);
        setAutoCroppedImage(null);
        setFinalImage(null);
        setCurrentFile(null);
        setCroppedAreaPixels(null);
        setRotation(0);
        setCurrentAspect(cropAspect);
        setViewMode(ViewMode.UPLOAD);
        setIsImageFile(false);
    }, [cropAspect]);

    // Process file and call onDone with the result
    const processFile = useCallback((file: File) => {
        setProcessing(true);
        setCurrentFile(file);
        
        // Check if file is an image
        const isImage = file.type.startsWith('image/');
        setIsImageFile(isImage);
        
        const reader = new FileReader();
        reader.onloadend = () => {
            if (typeof reader.result === 'string' && isImage) {
                // If it's an image, load it for processing
                const img = new Image();
                img.onload = () => {
                    // Save original image
                    setOriginalImage(reader.result as string);
                    
                    // Process the image to crop whitespace
                    const processedImageSrc = processImage(img);
                    setAutoCroppedImage(processedImageSrc);
                    
                    // Show auto-crop preview first if auto-crop is enabled
                    if (autoCrop) {
                        setViewMode(ViewMode.PREVIEW);
                        setFinalImage(processedImageSrc);
                        setProcessing(false);
                    } else if (allowManualCrop) {
                        // If auto-crop is disabled but manual is enabled, go straight to manual
                        setViewMode(ViewMode.MANUAL_CROP);
                        setProcessing(false);
                    } else {
                        // If both are disabled, just use the original image
                        setFinalImage(reader.result as string);
                        setViewMode(ViewMode.PREVIEW);
                        
                        onDone({
                            base64: reader.result as string,
                            file: {
                                name: file.name,
                                size: file.size,
                                type: file.type,
                                lastModified: file.lastModified,
                            },
                            name: file.name,
                            type: file.type,
                        });
                        
                        setUploaded(true);
                        setProcessing(false);
                    }
                };
                img.src = reader.result;
            } else {
                // For non-image files, just pass through the original data
                onDone({
                    base64: reader.result?.toString() || '',
                    file: {
                        name: file.name,
                        size: file.size,
                        type: file.type,
                        lastModified: file.lastModified,
                    },
                    name: file.name,
                    type: file.type,
                });
                setUploaded(true);
                setProcessing(false);
                setViewMode(ViewMode.FILE_INFO); // Use the new view mode for non-image files
            }
        };
        reader.readAsDataURL(file);
    }, [onDone, processImage, autoCrop, allowManualCrop]);

    // Handle file drop
    const fileDrop = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            setDragging(false);

            const files = e.dataTransfer.files;
            if (files.length > 0) {
                const file = files[0]; // Only take the first file
                processFile(file);
            }
        },
        [processFile]
    );

    // Handle file selection from input
    const handleFileSelect = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = e.target.files;
            if (files && files.length > 0) {
                const file = files[0]; // Only take the first file
                processFile(file);
            }
        },
        [processFile]
    );

    // Get pretty formatted file size
    const getFormattedFileSize = (bytes: number): string => {
        if (bytes < 1024) return bytes + ' bytes';
        else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        else if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
        else return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
    };

    // Render the appropriate view based on the current mode
    const renderCurrentView = () => {
        switch (viewMode) {
            case ViewMode.UPLOAD:
                return (
                    <UploadContainer
                        onDragOver={dragOver}
                        onDragEnter={dragEnter}
                        onDragLeave={dragLeave}
                        onDrop={fileDrop}
                        dragging={dragging}
                        uploaded={uploaded}
                        processing={processing}
                    >
                        {processing ? (
                            <>
                                <UploadIcon className="fa-duotone fa-spinner fa-spin" />
                                Processing...
                            </>
                        ) : (
                            <>
                                <UploadIcon className="fa-duotone fa-upload" />
                                Drag and Drop Files here
                                <OrText>or</OrText>
                                <BrowseButton>
                                    Browse Files
                                    <input 
                                        type="file"
                                        onChange={handleFileSelect}
                                        style={{ 
                                            opacity: 0,
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            cursor: 'pointer'
                                        }}
                                    />
                                </BrowseButton>
                            </>
                        )}
                    </UploadContainer>
                );
                
            case ViewMode.FILE_INFO:
                // New view for non-image files
                return (
                    <FileInfoContainer>
                        <FileInfoHeader>
                            <h3>File Uploaded</h3>
                        </FileInfoHeader>
                        
                        <FileInfoContent>
                            {currentFile && (
                                <>
                                    <FileIcon className="fa-duotone fa-file" />
                                    <FileDetails>
                                        <FileName>{currentFile.name}</FileName>
                                        <FileType>{currentFile.type || 'Unknown type'}</FileType>
                                        <FileSize>{getFormattedFileSize(currentFile.size)}</FileSize>
                                    </FileDetails>
                                </>
                            )}
                        </FileInfoContent>
                        
                        <FileInfoButtonGroup>
                            <ActionButton primary onClick={handleNewUpload}>
                                Upload New File
                            </ActionButton>
                        </FileInfoButtonGroup>
                    </FileInfoContainer>
                );
                
            case ViewMode.PREVIEW:
                return (
                    <PreviewContainer>
                        <PreviewHeader>
                            <h3>{autoCrop && isImageFile ? 'Auto-Cropped Image' : 'Image Preview'}</h3>
                        </PreviewHeader>
                        
                        <ImagePreview>
                            {finalImage && (
                                <img 
                                    src={finalImage} 
                                    alt="Processed" 
                                    style={{ maxWidth: '100%', maxHeight: `${previewMaxHeight}px`, objectFit: 'contain' }} 
                                />
                            )}
                        </ImagePreview>
                        
                        <PreviewButtonGroup>
                            {isImageFile && allowManualCrop && (
                                <ActionButton onClick={handleRecrop}>
                                    Adjust Crop Manually
                                </ActionButton>
                            )}
                            <ActionButton primary onClick={handleNewUpload}>
                                Upload New {isImageFile ? 'Image' : 'File'}
                            </ActionButton>
                        </PreviewButtonGroup>
                    </PreviewContainer>
                );
                
            case ViewMode.MANUAL_CROP:
                return (
                    <CropperContainer cropperHeight={cropperHeight}>
                        <CropperHeader>
                            <h3>Adjust Crop</h3>
                            <p>Drag to reposition. Use slider to zoom in/out.</p>
                        </CropperHeader>
                        
                        <CropperControlsLayout>
                            <ControlsSidebar>
                                <ControlGroupLabel>Rotate</ControlGroupLabel>
                                <ControlButton onClick={handleRotate} title="Rotate 90°">
                                    <i className="fa-duotone fa-rotate" />
                                </ControlButton>
                                
                                <ControlGroupLabel>Aspect Ratio</ControlGroupLabel>
                                {aspectOptions.map((option) => (
                                    <ControlButton 
                                        key={option.label}
                                        onClick={() => handleAspectChange(option.value)}
                                        active={currentAspect === option.value}
                                        title={option.label}
                                    >
                                        <i className={`fa-duotone ${option.icon}`} />
                                        <span>{option.label}</span>
                                    </ControlButton>
                                ))}
                            </ControlsSidebar>
                            
                            <CropperWrapper cropperHeight={cropperHeight}>
                                <Cropper
                                    image={originalImage || ''}
                                    crop={crop}
                                    zoom={zoom}
                                    aspect={currentAspect}
                                    rotation={rotation}
                                    onCropChange={setCrop}
                                    onCropComplete={onCropComplete}
                                    onZoomChange={setZoom}
                                    onRotationChange={setRotation}
                                    showGrid={true}
                                    objectFit="contain"
                                />
                            </CropperWrapper>
                        </CropperControlsLayout>
                        
                        <ZoomControl>
                            <ZoomLabel>Zoom</ZoomLabel>
                            <ZoomSlider 
                                type="range" 
                                value={zoom} 
                                min={1} 
                                max={3} 
                                step={0.1} 
                                onChange={(e) => setZoom(Number(e.target.value))} 
                            />
                        </ZoomControl>
                        
                        <CropperButtonGroup>
                            {autoCrop && autoCroppedImage && (
                                <CropperButton onClick={handleAcceptAutoCrop}>
                                    Use Auto-Crop
                                </CropperButton>
                            )}
                            <CropperButton primary onClick={handleCropConfirm}>
                                Confirm Crop
                            </CropperButton>
                        </CropperButtonGroup>
                    </CropperContainer>
                );
                
            default:
                return null;
        }
    };

    return (
        <OuterContainer cropperWidth={cropperWidth}>
            {renderCurrentView()}
            
            {/* Hidden canvases for image processing */}
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            <canvas ref={processedCanvasRef} style={{ display: 'none' }} />
        </OuterContainer>
    );
};

const OuterContainer = styled.div<{
    cropperWidth?: number;
}>`
    border-radius: 15px;
    width: ${props => props.cropperWidth || 420}px;
    background-color: #8cd071;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px;
`;

const UploadContainer = styled.div<{ 
    dragging: boolean; 
    uploaded: boolean;
    processing?: boolean;
}>`
    border-radius: 15px;
    width: 100%;
    height: 220px;
    background-color: ${p => 
        p.dragging ? 'white' : 
        p.processing ? '#e6f2df' :
        p.uploaded ? '#b9f99f' : 
        '#ccf5bc'
    };
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    font-size: 24px;
    position: relative;
`;

// New styled components for file info view
const FileInfoContainer = styled.div`
    border-radius: 15px;
    width: 100%;
    background-color: #fff;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const FileInfoHeader = styled.div`
    padding: 10px 15px;
    background-color: #e6f2df;
    
    h3 {
        margin: 0;
        font-size: 18px;
    }
`;

const FileInfoContent = styled.div`
    display: flex;
    padding: 30px;
    align-items: center;
    min-height: 150px;
    background-color: #f7f7f7;
`;

const FileIcon = styled.i`
    font-size: 48px;
    color: #6bbd4b;
    margin-right: 20px;
`;

const FileDetails = styled.div`
    display: flex;
    flex-direction: column;
`;

const FileName = styled.div`
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 5px;
    word-break: break-all;
`;

const FileType = styled.div`
    font-size: 14px;
    color: #666;
    margin-bottom: 5px;
`;

const FileSize = styled.div`
    font-size: 14px;
    color: #666;
`;

const FileInfoButtonGroup = styled.div`
    display: flex;
    padding: 15px;
    gap: 10px;
`;

const PreviewContainer = styled.div`
    border-radius: 15px;
    width: 100%;
    background-color: #fff;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const PreviewHeader = styled.div`
    padding: 10px 15px;
    background-color: #e6f2df;
    
    h3 {
        margin: 0;
        font-size: 18px;
    }
`;

const ImagePreview = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    background-color: #f7f7f7;
    min-height: 250px;
`;

const PreviewButtonGroup = styled.div`
    display: flex;
    padding: 15px;
    gap: 10px;
`;

const CropperContainer = styled.div<{
    cropperHeight?: number;
}>`
    border-radius: 15px;
    width: 100%;
    height: ${props => props.cropperHeight || 450}px;
    background-color: #fff;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const CropperHeader = styled.div`
    padding: 10px 15px;
    background-color: #e6f2df;
    
    h3 {
        margin: 0;
        font-size: 18px;
    }
    
    p {
        margin: 5px 0 0;
        font-size: 12px;
        color: #666;
    }
`;

const CropperControlsLayout = styled.div`
    display: flex;
    flex: 1;
    position: relative;
    overflow: hidden;
`;

const ControlsSidebar = styled.div`
    width: 80px;
    background-color: #f2f2f2;
    display: flex;
    flex-direction: column;
    padding: 10px;
    overflow-y: auto;
`;

const ControlGroupLabel = styled.div`
    font-size: 12px;
    color: #666;
    margin: 10px 0 5px;
    text-align: center;
    width: 100%;
`;

const ControlButton = styled.button<{
    active?: boolean;
}>`
    background-color: ${props => props.active ? '#6bbd4b' : '#e0e0e0'};
    color: ${props => props.active ? 'white' : '#333'};
    border: none;
    border-radius: 6px;
    padding: 8px 4px;
    margin-bottom: 8px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    
    &:hover {
        background-color: ${props => props.active ? '#5aa43a' : '#d0d0d0'};
    }
    
    i {
        font-size: 18px;
        margin-bottom: 4px;
    }
    
    span {
        font-size: 10px;
    }
`;

const CropperWrapper = styled.div<{
    cropperHeight?: number;
}>`
    position: relative;
    flex: 1;
    background-color: #000;
`;

const ZoomControl = styled.div`
    padding: 10px 15px;
    display: flex;
    align-items: center;
`;

const ZoomLabel = styled.span`
    font-size: 14px;
    margin-right: 10px;
    min-width: 45px;
`;

const ZoomSlider = styled.input`
    flex-grow: 1;
`;

const CropperButtonGroup = styled.div`
    display: flex;
    padding: 10px 15px;
    gap: 10px;
    margin-top: auto;
`;

const CropperButton = styled.button<{ primary?: boolean }>`
    flex: 1;
    padding: 10px 0;
    border-radius: 8px;
    border: none;
    font-size: 14px;
    cursor: pointer;
    background-color: ${props => props.primary ? '#6bbd4b' : '#e0e0e0'};
    color: ${props => props.primary ? 'white' : '#333'};
    
    &:hover {
        background-color: ${props => props.primary ? '#5aa43a' : '#d0d0d0'};
    }
`;

const ActionButton = styled.button<{ primary?: boolean }>`
    flex: 1;
    padding: 12px 0;
    border-radius: 8px;
    border: none;
    font-size: 14px;
    cursor: pointer;
    background-color: ${props => props.primary ? '#6bbd4b' : '#e0e0e0'};
    color: ${props => props.primary ? 'white' : '#333'};
    
    &:hover {
        background-color: ${props => props.primary ? '#5aa43a' : '#d0d0d0'};
    }
`;

const UploadIcon = styled.i`
    font-size: 28px;
    margin-bottom: 10px;
`;

const OrText = styled.div`
    font-size: 16px;
    margin: 10px 0;
    color: #666;
`;

const BrowseButton = styled.div`
    background-color: #6bbd4b;
    color: white;
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 16px;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    
    &:hover {
        background-color: #5aa43a;
    }
`;

export default DragAndDropFileUpload;